import { I18n } from "@assets/strings";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Group } from "@domain/group/group";
import { useMyDiscussions, useMySharerooms } from "@domain/group/hooks";
import { fadeIn } from "@ui/styles/keyframes";
import { gap, mobile } from "@ui/styles/utils";
import { PrimaryBold } from "@ui/components/containers/primary";
import { ChevronDownIcon } from "@ui/components/icons";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { row } from "@ui/components/layout";
import { BackButton } from "@ui/components/navigation/backButton";

interface PostTargetSelectionProps extends React.HTMLAttributes<HTMLDivElement> {
	onTargetChosen: (target: Group | null) => void;
	cancel: () => void;
}

export const PostTargetSelection: React.FC<PostTargetSelectionProps> = ({ cancel, onTargetChosen, ...htmlProps }) => {
	const {
		result: sharerooms,
		totalElements: totalSharerooms,
		loading: shareroomsLoading,
		fetchMore: fetchMoreSharerooms,
	} = useMySharerooms();
	const {
		result: discussions,
		totalElements: totalDiscussions,
		loading: discussionsLoading,
		fetchMore: fetchMoreDiscussions,
	} = useMyDiscussions();

	const [step, setStep] = useState<"SWITCH" | "SHAREROOM" | "DISCUSSION">("SWITCH");
	const groupsRef = useRef<HTMLDivElement>(null);

	return (
		<Container {...htmlProps}>
			<BackButton onClick={() => (step === "SWITCH" ? cancel() : setStep("SWITCH"))} />
			{step === "SWITCH" ? (
				<TargetContainers>
					<TargetRow onClick={() => onTargetChosen(null)}>{I18n.t("post.target.default")}</TargetRow>
					{totalSharerooms !== 0 && (
						<TargetRow disabled={shareroomsLoading} onClick={() => setStep("SHAREROOM")}>
							<PrimaryBold>{I18n.t("post.target.shareroom")}</PrimaryBold>
							<ChevronDownIcon />
						</TargetRow>
					)}
					{totalDiscussions !== 0 && (
						<TargetRow disabled={discussionsLoading} onClick={() => setStep("DISCUSSION")}>
							<PrimaryBold>{I18n.t("post.target.discussion")}</PrimaryBold>
							<ChevronDownIcon />
						</TargetRow>
					)}
				</TargetContainers>
			) : (
				<Groups ref={groupsRef}>
					<InfiniteScroll
						loading={shareroomsLoading || discussionsLoading}
						onEndReached={() => (step === "SHAREROOM" ? fetchMoreSharerooms() : fetchMoreDiscussions())}
						scrollingElement={groupsRef}
					>
						{(step === "SHAREROOM" ? sharerooms : discussions)
							.filter((group) => group.status === "VALIDATED")
							.map((group) => (
								<TargetRow
									key={group.id}
									onClick={() => {
										setStep("SWITCH");
										onTargetChosen(group);
									}}
								>
									{group.name}
								</TargetRow>
							))}
					</InfiniteScroll>
				</Groups>
			)}
		</Container>
	);
};

const Container = styled.div`
	${gap(20)};
	margin: 0 -65px;
	margin-bottom: -50px;
	> :first-child {
		margin-left: 65px;
	}

	@media ${mobile} {
		margin: 0 -20px;
		> :first-child {
			margin-left: 20px;
		}
	}
`;

const TargetContainers = styled.div`
	margin-bottom: 50px;
	animation: ${fadeIn} 0.4s linear;
`;

const TargetRow = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
	cursor: pointer;
	${({ disabled }) => disabled && "pointer-events: none;"}
	padding: 20px 65px;
	@media ${mobile} {
		padding: 20px;
	}
	${row(10, "space-between", "center")};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray};

	> svg {
		transform: rotate(-90deg);
	}
`;

const Groups = styled.div`
	overflow: auto;
	/* border-radius: 6px; */
	border-top: 1px solid ${({ theme }) => theme.colors.gray};
	height: 300px;
	animation: ${fadeIn} 0.4s linear;

	:last-child {
		border-bottom: none;
	}
`;
